import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";

interface ThreadMessage {
  id: string;
  author_display_name: string;
  is_external: boolean;
  body_markdown: string;
  created_at: string;
}

interface TicketFull {
  public_id: string;
  title: string;
  body_markdown: string;
  state: { display_name: string; category: string; is_terminal: boolean };
  product: { display_name: string };
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  thread_messages: ThreadMessage[];
}

const STATE_BADGE: Record<string, string> = {
  open: "bg-blue-100 text-blue-800",
  pending: "bg-amber-100 text-amber-900",
  resolved: "bg-emerald-100 text-emerald-900",
  closed: "bg-slate-200 text-slate-700",
};

export default function TicketViewPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["portal-ticket", publicId],
    queryFn: () => api<TicketFull>(`/portal/tickets/${publicId}`),
    enabled: Boolean(publicId),
  });

  const m = useMutation({
    mutationFn: () =>
      api(`/portal/tickets/${publicId}/messages`, {
        method: "POST",
        body: JSON.stringify({ body_markdown: reply.trim() }),
      }),
    onSuccess: () => {
      setReply("");
      qc.invalidateQueries({ queryKey: ["portal-ticket", publicId] });
    },
  });

  if (isLoading) return <div className="text-slate-600">Loading…</div>;
  if (!data) return <div className="text-slate-600">Not found.</div>;

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        <Link to="/my-tickets" className="underline">← My tickets</Link>
      </div>
      <header className="space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-mono text-blue-600">{data.public_id}</span>
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${STATE_BADGE[data.state.category] ?? "bg-slate-100"}`}
          >
            {data.state.display_name}
          </span>
          <span className="text-slate-500">{data.product.display_name}</span>
        </div>
        <h1 className="text-2xl font-semibold">{data.title}</h1>
      </header>

      <article className="rounded-md border border-slate-200 bg-white p-4 text-sm">
        <header className="mb-2 text-xs text-slate-500">
          You · {new Date(data.created_at).toLocaleString()}
        </header>
        <pre className="whitespace-pre-wrap font-sans">{data.body_markdown}</pre>
      </article>

      {data.thread_messages.map((m) => (
        <article key={m.id} className="rounded-md border border-slate-200 bg-white p-4 text-sm">
          <header className="mb-2 text-xs text-slate-500">
            {m.author_display_name} · {new Date(m.created_at).toLocaleString()}
          </header>
          <pre className="whitespace-pre-wrap font-sans">{m.body_markdown}</pre>
        </article>
      ))}

      {data.state.is_terminal ? (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
          This ticket is closed. <Link to="/new" className="text-blue-600 underline">File a new one</Link>.
        </div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reply.trim()) m.mutate();
          }}
          className="space-y-2"
        >
          <textarea
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            rows={4}
            placeholder="Reply…"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={!reply.trim() || m.isPending}
            className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
          >
            {m.isPending ? "Sending…" : "Send reply"}
          </button>
        </form>
      )}
    </div>
  );
}
